/* wg-sticky-ab.js - Wade Greens
   -----------------------------------------------------------------------
   Moves the announcement bar INTO the header so the two travel together.

   WHY
   "PROUDLY GROWN IN ALABAMA!" is not part of the header. It is painted by
   a separate section, sections/announcement-bar, whose inner container is
   position:absolute with top:0 and an offsetParent of <body> - so it is
   pinned to the top of the DOCUMENT, not to the viewport and not to the
   header. At the top of the page it happens to land exactly over the
   header's own black utility row, which is why the two read as one band.
   The moment you scroll, the header stays (it is position:sticky) and the
   announcement bar slides away with the page.

   The header is data-sticky-type="on_scroll": it hides on the way down and
   slides back on the way up. So a CSS-only fix would have to pin the
   announcement bar with position:fixed AND track the header's hide/show
   state, or it would hover over the page on its own while the header is
   away.

   Re-parenting avoids all of that. Once the container is a child of
   .header-top--wrapper it inherits the header's sticky behaviour, its
   hide-and-reveal, and its z-index for free. No scroll listener, no state
   classes to mirror, nothing to keep in sync.

   GEOMETRY IS PRESERVED, not re-created. wg-sticky-header-full.css gives
   .header-top--wrapper position:relative and re-anchors the container with
   left:50% and a -50% translate, so it keeps its own .container width
   instead of stretching. Measured at 1440: L432 W576 H36 T0 both before
   and after the move - the same box, to the pixel. The container is
   absolutely positioned, so it is out of flow and the flex row underneath
   it is untouched; the modal buttons and the account/wishlist/cart icons
   do not move.

   -----------------------------------------------------------------------
   THE SLIDER, AND WHY setPosition ALONE IS NOT ENOUGH

   The messages rotate on slick. Slick caches slideWidth at init and writes
   the track's offset as a transform in pixels. Moving the container
   changes its width, so the cached offset goes stale and every slide sits
   a few dozen pixels off - you see the tail of one message and the head of
   the next, which is what the clipped "...Applies At Checkout" looked
   like.

   Measured in that state: listWidth and slideWidth had both correctly
   updated to 576, but the track transform still read -1056.15px, which is
   -(2 x 528.075) - two slides at the PRE-MOVE width.

   The cause was an ordering one. Slick initialises AFTER this script moves
   the node, so a setPosition() fired at move time found no
   .slick-initialized element and did nothing at all. The bar then stayed
   wrong until the next autoplay tick happened to rewrite the transform.
   So the script now WAITS for slick to exist before re-measuring, and
   re-measures again on the next frame and on two short timers, because the
   width that matters is only final once the browser has applied the
   stylesheet rules to the moved node.

   setPosition() is the only slick call made here, deliberately.
   slickGoTo(current, true) was tried as a way to force the transform and
   it wedged the slider: the track froze at a single offset while
   .slick-current kept cycling, so the messages changed class without ever
   sliding. setPosition() recomputes the dimensions AND re-applies the
   offset from them, which is all this needs.

   -----------------------------------------------------------------------
   DESKTOP ONLY, matching the CSS. Below 1081px the theme hides
   .header-top--wrapper outright and the phone uses its own header section,
   so a container parked in there would disappear. The media query is
   watched and the node is put back at its original spot - marked with a
   comment node - if the window narrows.

   Also re-applied on shopify:section:load so the theme editor does not
   strand a stale copy when either section is edited.
   ----------------------------------------------------------------------- */

(function () {
  'use strict';

  /* 1081, not 1025: component-header-nav-multi-site.css hides
     .header-top--wrapper outright below 1081px, so a banner parked in it
     would vanish with it. wg-sticky-header-full.css uses the same floor. */
  var MQ = window.matchMedia('(min-width: 1081px)');
  var node = null;
  var anchor = null;
  var waits = 0;

  function findNode() {
    return document.querySelector('.announcement-bar .container.layout--slider') ||
           document.querySelector('.header-nav-multi-site .header-top--wrapper > .container.layout--slider');
  }

  /* Re-seat the slick track against the container's CURRENT width. */
  function reseatSlider() {
    var $ = window.jQuery;
    if (!$ || !node) return;

    var slider = $(node).find('.slick-initialized');

    if (!slider.length) {
      /* slick has not run yet - check back, but do not wait forever */
      if (waits++ < 20) { setTimeout(reseatSlider, 150); }
      return;
    }
    waits = 0;

    var fix = function () {
      try {
        slider.slick('setPosition');
      } catch (e) { /* slider was torn down, nothing to re-seat */ }
    };

    fix();
    if (window.requestAnimationFrame) { window.requestAnimationFrame(fix); }
    setTimeout(fix, 120);
    setTimeout(fix, 500);
  }

  /* ---------------------------------------------------------------------
     CARRY THE THEME'S TYPE SETTINGS ACROSS THE MOVE

     The announcement bar's colour and type come from the section settings,
     which Shopify renders as an inline rule scoped to the SECTION's id:

       #shopify-section-sections--<id>__announcement-bar .announcement-bar__message
         { color: rgb(255,255,255); font-size: 12px; font-weight: 400; ... }

     Move the container out of that section and the selector stops
     matching. The messages lose the white and inherit black from the page
     - black text on the black bar, invisible - and lose the 12px sizing
     with it.

     So the computed values are read off a real message BEFORE the move,
     while the rule still applies, and written onto the container as
     custom properties. wg-sticky-header-full.css feeds them back to the
     messages in their new home. Reading the COMPUTED value rather than
     hardcoding white is the point: whatever colour is picked in the theme
     editor is what gets carried, with no second place to keep in sync.
     --------------------------------------------------------------------- */
  var TYPE_PROPS = [
    ['color',          '--wg-ab-color'],
    ['fontSize',       '--wg-ab-size'],
    ['fontWeight',     '--wg-ab-weight'],
    ['fontStyle',      '--wg-ab-style'],
    ['letterSpacing',  '--wg-ab-tracking']
  ];

  function captureType() {
    if (!node) return;
    var sample = node.querySelector('.announcement-bar__message');
    if (!sample) return;
    var cs = window.getComputedStyle(sample);
    for (var i = 0; i < TYPE_PROPS.length; i++) {
      var v = cs[TYPE_PROPS[i][0]];
      if (v) { node.style.setProperty(TYPE_PROPS[i][1], v); }
    }
  }

  function releaseType() {
    if (!node) return;
    for (var i = 0; i < TYPE_PROPS.length; i++) {
      node.style.removeProperty(TYPE_PROPS[i][1]);
    }
  }

  function moveIn() {
    var wrap = document.querySelector('.header-nav-multi-site .header-top--wrapper');
    if (!wrap || !node) return;
    if (node.parentNode === wrap) return;
    if (!anchor) {
      anchor = document.createComment('wg-ab-home');
      node.parentNode.insertBefore(anchor, node);
    }
    captureType();          /* must run while the section rule still applies */
    wrap.appendChild(node);
    reseatSlider();
  }

  function moveBack() {
    if (!node || !anchor || !anchor.parentNode) return;
    if (node.parentNode === anchor.parentNode) return;
    anchor.parentNode.insertBefore(node, anchor);
    releaseType();          /* home again - the section's own rule applies */
    reseatSlider();
  }

  function apply() {
    node = findNode();
    if (!node) return;
    if (MQ.matches) { moveIn(); } else { moveBack(); }
  }

  function start() {
    apply();
    if (MQ.addEventListener) {
      MQ.addEventListener('change', apply);
    } else if (MQ.addListener) {
      MQ.addListener(apply);
    }
    /* Belt and braces. A matchMedia 'change' event is the right signal,
       but it does not always arrive when the viewport is being emulated
       rather than dragged - observed in testing, where the window went
       from 1440 to 1024 and the banner stayed parked in a header that had
       just been hidden. A throttled resize check costs nothing and cannot
       be missed. */
    var pending = null;
    window.addEventListener('resize', function () {
      if (pending) return;
      pending = setTimeout(function () { pending = null; apply(); }, 150);
    }, { passive: true });

    document.addEventListener('shopify:section:load', function () {
      anchor = null;
      setTimeout(apply, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(start, 0); });
  } else {
    setTimeout(start, 0);
  }

  /* Late webfonts and images can still shift the row - re-seat once more. */
  window.addEventListener('load', function () { apply(); reseatSlider(); });
})();
